import { z } from 'zod'

import { RuleID } from '@forto-dev/insights-engine-models'
import { CustomerAccountNotificationResponse } from '@forto-dev/notification-center-models'

import { TRANSPORT_TYPES, TransportType } from '@/features/shipments'

import { NOTIFICATION_PREFERENCE_TYPES } from './settings'

export const NOTIFICATION_STATUSES = ['new', 'unread', 'read'] as const
export const NotificationStatusEnum = z.enum(NOTIFICATION_STATUSES)

export type NotificationStatus = z.infer<typeof NotificationStatusEnum>

export const NOTIFICATION_TYPES = ['message', ...NOTIFICATION_PREFERENCE_TYPES] as const
export const NotificationTypeEnum = z.enum(NOTIFICATION_TYPES)
export type NotificationType = z.infer<typeof NotificationTypeEnum>

export type NotificationCounterParty = 'shipper' | 'consignee'

export type APINotification = {
  id: string
  status: NotificationStatus
  createdAt: string
  customerReference?: string
  transportType?: TransportType
  incoterm?: string
  shipperName?: string
  consignee?: string
  counterParty?: NotificationCounterParty
  type: NotificationType
  title: string
}

export type APINotificationResponse = {
  results: APINotification[]
  total: number
  page: number
  pageSize: number
}

export type APINotificationPreview = {
  id: string
  reference: RuleID
  customerAccountNotification: CustomerAccountNotificationResponse
}

const NotificationContainerSchema = z.object({
  containerId: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  type: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  containerNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  commodityType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  customsCleared: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
})

const NotificationCargoSchema = z.object({
  type: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  quantity: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  grossWeight: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  containerType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  width: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  height: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  length: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  volume: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  commodityType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  commodityDetails: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  specification: z
    .any()
    .nullish()
    .transform((data) => data ?? undefined),
})

export const NotificationContextSchema = z.object({
  customerReference: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  transportType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  transportMode: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  incoterm: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  bookingDate: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  consignee: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  consigneeEmail: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  shipper: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  shipperEmail: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  destination: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  origin: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  tsOrigin: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  tsDestination: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  destinationOpsId: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  originOpsId: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  carrier: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  serviceType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  customsType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  tradeClass: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  customerName: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  containers: z
    .array(NotificationContainerSchema)
    .nullish()
    .transform((data) => data ?? []),
  cargo: z
    .array(NotificationCargoSchema)
    .nullish()
    .transform((data) => data ?? undefined),
  carrierName: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  vesselName: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  pod: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  pol: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  bookingParty: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  bookingInitiatorName: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  cargoReadyDate: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  quoteCode: z
    .string()
    .nullish()
    .transform((data) => data ?? null),
})

export const NotificationPreviewSchema = z.object({
  id: z.string(),
  reference: z.nativeEnum(RuleID).or(z.string()),
  customerAccountNotification: z.object({
    id: z.string(),
    customerID: z.string(),
    insightID: z
      .string()
      .nullish()
      .transform((data) => data ?? undefined),
    createdAt: z.string(),
    type: z.string(),
    entity: z.object({
      type: z.string(),
      id: z.string(),
    }),
    ruleID: z.string(),
    data: z
      .object({
        activity: z
          .object({
            actor: z.object({
              type: z.string(),
              id: z
                .string()
                .nullish()
                .transform((data) => data ?? undefined),
              fullName: z
                .string()
                .nullish()
                .transform((data) => data ?? undefined),
              avatar: z
                .string()
                .nullish()
                .transform((data) => data ?? undefined),
              email: z
                .string()
                .nullish()
                .transform((data) => data ?? undefined),
            }),
            createdAt: z.string(),
            updatedAt: z
              .date()
              .nullish()
              .transform((data) => data ?? undefined),
            customField: z
              .object({})
              .passthrough()
              .nullish()
              .transform((data) => data ?? undefined),
            object: z.object({
              type: z.string(),
              id: z
                .string()
                .nullish()
                .transform((data) => data ?? undefined),
            }),
            type: z.string(),
            to: z
              .array(z.string())
              .nullish()
              .transform((data) => data ?? undefined),
          })
          .nullish()
          .transform((data) => data ?? undefined),
        activityVisibility: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        fieldValues: z
          .any()
          .nullish()
          .transform((data) => data ?? undefined),
        workflow: z
          .any()
          .nullish()
          .transform((data) => data ?? undefined),
      })
      // passthrough() Because we want to keep all fields in the data object, even ones added down the line, and they're fully handled within the renderer-sdk so we should not need any updates to this parsing logic
      .passthrough(),
    context: NotificationContextSchema
      // @see https://github.com/freight-hub/ship-v2/blob/6edeff69521608818e3095a7722df21e1aa4932a/src/features/notifications/domain/notification.ts#L193 for why passthrough
      .passthrough()
      .nullish()
      .transform((data) => data ?? undefined),
  }),
})
export type NotificationContext = z.infer<typeof NotificationContextSchema>
export type NotificationPreview = z.infer<typeof NotificationPreviewSchema>

export const NotificationSchema = z.object({
  id: z.string(),
  status: NotificationStatusEnum,
  createdAt: z.coerce.date(),
  customerReference: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  transportType: z
    .enum(TRANSPORT_TYPES)
    .or(z.string())
    .nullish()
    .transform((data) => data || undefined),
  incoterm: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  shipperName: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  consignee: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  counterParty: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  type: NotificationTypeEnum,
  title: z.string(),
})
export type Notification = z.infer<typeof NotificationSchema>

export const NotificationsResponseSchema = z.object({
  results: z.array(NotificationSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
})

export type NotificationsResponse = z.infer<typeof NotificationsResponseSchema>

export type APINotificationsCount = {
  new: number
  unread: number
}

export const NotificationCountSchema = z.object({
  new: z.number(),
  unread: z.number(),
})

export type NotificationCount = z.infer<typeof NotificationCountSchema>
